// @docs:
//   - docs/core/architecture/PLAN_OVERRIDES.md
// ==============================================
// At a glance, in the admin Users list: is this customer on the plan they pay
// for? Nothing is shown when they are. The state comes from the API
// (vortex-api lib/planBilling.ts).
// ==============================================

export type PlanBillingState = 'paid' | 'overridden' | 'mismatch' | 'not_billed' | 'unknown';

export interface PlanBillingSummary {
  billing_state?: PlanBillingState;
  billed_package_name?: string | null;
  plan_override_by_name?: string | null;
  plan_override_at?: string | null;
  plan_override_note?: string | null;
}

const pill = 'inline-flex items-center px-2 py-0.5 rounded text-[13px] font-medium border';

function recorded(sub: PlanBillingSummary): string {
  const parts: string[] = [];
  if (sub.plan_override_by_name) parts.push(`by ${sub.plan_override_by_name}`);
  if (sub.plan_override_at) parts.push(new Date(sub.plan_override_at).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' }));
  const who = parts.length ? `Set ${parts.join(', ')}` : '';
  return [who, sub.plan_override_note ?? ''].filter(Boolean).join(' — ');
}

export function PlanBillingBadge({ sub, onMarkOverride, busy = false }: {
  sub: PlanBillingSummary;
  onMarkOverride?: () => void;
  busy?: boolean;
}) {
  const pays = sub.billed_package_name?.trim() || 'another plan';
  const note = recorded(sub);
  switch (sub.billing_state) {
    case 'overridden':
      return (
        <div className="mt-1.5 space-y-0.5">
          <span className={`${pill} bg-amber-500/10 text-amber-300 border-amber-500/30`} title={note || undefined}>Override · pays {pays}</span>
          {sub.plan_override_note && <p className="text-[13px] text-slate-400 max-w-[14rem] truncate" title={note}>{sub.plan_override_note}</p>}
        </div>
      );
    case 'mismatch':
      return (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <span className={`${pill} bg-red-500/10 text-red-300 border-red-500/30`} title="Stripe charges for a different plan, and no override was recorded.">Mismatch · pays {pays}</span>
          {onMarkOverride && (
            <button type="button" onClick={onMarkOverride} disabled={busy}
              className="text-[13px] text-cyan-400 hover:text-cyan-300 underline-offset-2 hover:underline disabled:opacity-50">
              Mark as override
            </button>
          )}
        </div>
      );
    case 'not_billed':
      return (
        <div className="mt-1.5">
          <span className={`${pill} bg-slate-700/60 text-slate-300 border-slate-600`} title={note || 'No Stripe subscription: given in admin, or set up by hand.'}>Not billed</span>
        </div>
      );
    case 'unknown':
      return (
        <div className="mt-1.5">
          <span className={`${pill} bg-slate-700/60 text-slate-400 border-slate-600`} title="Billed in Stripe, but which plan is not recorded yet. Use “Read billed plans from Stripe”.">Billing unknown</span>
        </div>
      );
    default:
      return null;
  }
}
